import React, { FC } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { PlayCircle } from '@mui/icons-material';
import { CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { CountryCode } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useShouldForceVideoLanguage } from '~/core/hooks/useShouldForceVideoLanguage';
import { getEnvironmentAwareHref, showGameCenterLink, showTicketsLink } from '~/core/utils';
import { useGetWebStreamURLQuery } from '~/redux/ducks/webStreamURL';
import StrictWidthText from '~/views/components/strictWidthText/StrictWidthText';

import { ScoresGame } from '../../../../types';
import { BottomBar, LinkContainer } from '../../styles';

const LinksBar: FC<{ game: ScoresGame; countryCode: CountryCode }> = ({ game, countryCode }) => {
    const isWebview = useIsWebview();
    const intl = useIntl();
    const shouldForceVideoLanguage = useShouldForceVideoLanguage();

    const liveGame = CoreUtils.isGameLive(game.gameState);
    const postGame = CoreUtils.isPostGame(game.gameState);

    const gameCenterLink = game.gameCenterLink && getEnvironmentAwareHref(game.gameCenterLink, isWebview);

    const threeMinuteRecap = useOptionForLocale(
        {
            [ShortCodeEnums.Language.English]: game.threeMinRecap,
            [ShortCodeEnums.Language.French]: game.threeMinRecapFr,
        },
        shouldForceVideoLanguage
    );

    const condensedGame = useOptionForLocale(
        {
            [ShortCodeEnums.Language.English]: game.condensedGame,
            [ShortCodeEnums.Language.French]: game.condensedGameFr,
        },
        shouldForceVideoLanguage
    );

    const ticketsLink = useOptionForLocale({
        [ShortCodeEnums.Language.English]: game.ticketsLink,
        [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
    });

    const { data: streamUrl } = useGetWebStreamURLQuery({
        broadcasts: game.tvBroadcasts,
        countryCode,
        language: intl.locale as ShortCodeEnums.Language,
    });

    const isGameCenterShown = showGameCenterLink(game);
    const isTicketsLinkShown = showTicketsLink(game);
    const isThreeMinuteRecapShown = postGame && threeMinuteRecap;
    const isCondensedGameShown = postGame && condensedGame;
    const isStreamShown = liveGame && streamUrl;

    return (
        (isGameCenterShown || isTicketsLinkShown || isThreeMinuteRecapShown || isStreamShown) && (
            <BottomBar>
                {isTicketsLinkShown && (
                    <LinkContainer className="tickets-link" href={ticketsLink} target="_blank" rel="noreferrer">
                        <FormattedMessage {...CommonMessages.linkDescriptions.tickets} />
                    </LinkContainer>
                )}
                {isStreamShown && (
                    <LinkContainer className="stream-link" href={streamUrl} target="_blank" rel="noreferrer">
                        <FormattedMessage {...CommonMessages.linkDescriptions.stream} />
                    </LinkContainer>
                )}
                {isThreeMinuteRecapShown && (
                    <LinkContainer className="recap-link" href={threeMinuteRecap}>
                        <PlayCircle />
                        <StrictWidthText text={intl.formatMessage(CommonMessages.linkDescriptions.recap)} />
                    </LinkContainer>
                )}
                {isCondensedGameShown && (
                    <LinkContainer className="condensed-link" href={condensedGame}>
                        <PlayCircle />
                        <StrictWidthText text={intl.formatMessage(CommonMessages.linkDescriptions.condensed)} />
                    </LinkContainer>
                )}
                <LinkContainer className="gamecenter-link" href={gameCenterLink} $isHidden={!isGameCenterShown}>
                    <FormattedMessage {...CommonMessages.linkDescriptions.gamecenter} />
                </LinkContainer>
            </BottomBar>
        )
    );
};

export default LinksBar;
